import { Select } from '../atoms';

/**
 * MultiSelectChips — pick any number of values from a list (spec 0011: a client's
 * categories). The chosen values show as removable chips, in the order they were picked;
 * an "Add…" dropdown offers the values not yet chosen. A chosen value the list no longer
 * offers stays as a chip, so opening an old record never silently drops what it carries.
 *
 * Rendered as a labelled group (role="group", aria-label = `label`) so the whole control is
 * reachable by its field label.
 */
export interface MultiSelectChipsProps {
  label: string;
  options: { value: string; label: string }[];
  value: string[];
  onChange: (next: string[]) => void;
  /** Placeholder for the add dropdown. */
  addLabel?: string;
}

export function MultiSelectChips({ label, options, value, onChange, addLabel = 'Add…' }: MultiSelectChipsProps) {
  const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  const remaining = options.filter((o) => o.value !== '' && !value.includes(o.value));
  return (
    <div role="group" aria-label={label} style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-1)', alignItems: 'center' }}>
      {value.map((v) => (
        <span
          key={v}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 4px 2px 10px',
            borderRadius: 'var(--r-full)', background: 'var(--brand-primary-bg)',
            border: '1px solid var(--brand-primary)', color: 'var(--color-ink)', fontSize: 'var(--fs-sm)',
          }}
        >
          {labelFor(v)}
          <button
            type="button"
            aria-label={`Remove ${labelFor(v)}`}
            onClick={() => onChange(value.filter((x) => x !== v))}
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer', padding: '0 4px',
              color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)', lineHeight: 1, fontFamily: 'inherit',
            }}
          >×</button>
        </span>
      ))}
      {value.length === 0 && (
        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>None</span>
      )}
      {remaining.length > 0 && (
        <Select
          aria-label={`Add to ${label}`}
          size="sm"
          value=""
          placeholder={addLabel}
          options={remaining}
          onChange={(e) => { if (e.target.value) onChange([...value, e.target.value]); }}
          style={{ width: 'auto', minWidth: 120 }}
        />
      )}
    </div>
  );
}
